import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMuiModal } from '../../../_core/hooks';
import { CreateSurveyTemplateDto, SurveyTemplateDto, UpdateSurveyTemplateDto } from '@families-link/shared';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { Theme } from '@mui/material';

export type useOpenConfirmModalFormHookProps = {
	title: string;
	description?: string;
	icon?: ReactNode;
	danger?: boolean;
	info?: boolean;
	submitButtonName?: string;
	toastText?: string;
	needToast?: boolean;
	featchAction(values: any): Promise<void>;
	handlePagination?(): void;
	customBackgrondColorKey?: keyof Theme['colors'];
	customColorKey?: keyof Theme['colors'];
	customBorderColorKey?: keyof Theme['colors'];
};

export function useOpenConfirmModal(props: useOpenConfirmModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		title,
		description,
		icon,
		danger = false,
		info=false,
		submitButtonName,
		toastText,
		needToast = true,
		customBackgrondColorKey,
		customBorderColorKey,
		customColorKey,
	} = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const { openModal, closeModal } = useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleConfirm = async (surveyTemplate: SurveyTemplateDto): Promise<void> => {
		try {
			await featchAction(surveyTemplate);
			needToast && showToast(toastText ?? 'Successfully performed', 'success');
			handlePagination && handlePagination();
			closeModal();
		} catch (error) {
			console.error(error);
		}
	};

	const handleOpenConfirmModal = useCallback(
		(surveyTemplate: SurveyTemplateDto) => {
			const handleSubmit = async () => {
				await handleConfirm(surveyTemplate);
			};
			openModal({
				title,
				description: description?.replace(':SurveyTemplateName', surveyTemplate.title),
				onSubmit: handleSubmit,
				onClose: closeModal,
				icon,
				customBackgrondColorKey: customBackgrondColorKey,
				customColorKey: customColorKey,
				customBorderColorKey: customBorderColorKey,
				danger,
				leftButtonProps: {
					children: t('survey-templates.forms.buttons.cancel'),
					variant: 'secondary',
				},
				rightButtonProps: {
					children: submitButtonName,
					variant: danger? 'danger': info? 'info-light' : 'primary',
					contraModeStyleButton: true
				},
			});
		},
		[handleConfirm, openModal]
	);

	return handleOpenConfirmModal;
}
